import styled from "styled-components";
import css from "@styled-system/css";

import Popover from "../MenuPopover/MenuPopover";
import Icon from "../Icon/Icon";
import Box from "../Box/Box";
import Link from "../Link/Link";

import { MyBCALinks } from "./bcaHeader.type";
import MyBCAUser from "./MyBCAUser";

interface MyBCAPopoverProps {
  links: MyBCALinks;
  logOutUrl: string;
  user?: { firstName: string; lastName: string };
  isVisible: boolean;
}

const HeadingLink = styled(Link)`
  ${css({
    display: "flex",
    alignItems: "center",
    color: "primary-light-blue",
    fontSize: "body-small",
    fontWeight: "semiBold",
    lineHeight: 1.2,
  })}
`;

const BasicLink = styled(Link)`
  display: flex;
  align-items: center;
  line-height: 24px;
  ${css({
    fontSize: "body-small",
    color: "grey-blue",
    fontFamily: "normal",
    textDecoration: "none",
    outline: "none",

    "&:hover": {
      color: "grey-blue",
      textDecoration: "underline",
    },
    "&:focus-within": {
      color: "grey-blue",
      textDecoration: "underline",
    },
  })};
`;

const Divider = styled.hr`
  width: 100%;
  height: 2px;
  border: none;
  margin-top: 10px;
  ${css({
    backgroundColor: "divider-color",
  })}
`;

const MyBCAPopover = ({
  links,
  logOutUrl,
  user,
  isVisible,
}: MyBCAPopoverProps) => {
  const { selling, buying, accountLink } = links;

  const sellingLinks = selling?.links.filter((link) => link.isVisible);
  const buyingLinks = buying?.links.filter((link) => link.isVisible);

  if (!isVisible) {
    return null;
  }

  return (
    <Box
      as="section"
      pt="6px"
      css={`
        transform: translateX(15px);
        position: absolute;
        top: 100%;
        right: 0;
        z-index: 1;
      `}
    >
      <Popover
        height="auto"
        width="auto"
        as="ul"
        css={`
          height: auto;
          width: auto;
          box-sizing: border-box;
          position: relative;
          visibility: hidden;
          padding: 24px 32px;
          white-space: nowrap;
          display: flex;
          flex-direction: column;
          margin: 0;
          min-width: 380px;
          &:before {
            left: calc(100% - 43px);
          }
        `}
      >
        <MyBCAUser
          user={user}
          accountLink={accountLink}
          fontSize="body-small"
        />
        {(selling || buying) && (
          <Box display="flex" flexWrap="wrap" mb={1}>
            {buying && buying.isVisible && (
              <Box flex={1} pr={3}>
                <HeadingLink key={buying.id} href={buying.link}>
                  {buying.label}
                  <Icon name="arrow-right" height={14} ml={3} />
                </HeadingLink>
                <Divider />
                {buyingLinks?.map((link) => (
                  <BasicLink key={link.id} href={link.link}>
                    {link.label}
                  </BasicLink>
                ))}
              </Box>
            )}
            {selling && selling.isVisible && (
              <Box flex={1} pl={3}>
                <HeadingLink key={selling.id} href={selling.link}>
                  {selling.label}
                  <Icon name="arrow-right" height={14} ml={3} />
                </HeadingLink>
                <Divider />
                {sellingLinks?.map((link) => (
                  <BasicLink key={link.id} href={link.link}>
                    {link.label}
                  </BasicLink>
                ))}
              </Box>
            )}
          </Box>
        )}
        <Divider />
        <BasicLink href={logOutUrl} target="_top">
          <Icon name="sign-out" color="grey-blue" height={18} mr={2} />
          Sign Out
        </BasicLink>
      </Popover>
    </Box>
  );
};

export default MyBCAPopover;
